import { useMemo, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getProviders } from 'app/store/actions/providers/providers.actions';
import { getCredentials } from 'app/main/credentials/store/actions/credential.actions';

export default function useProviderData(providerId) {
    const dispatch = useDispatch();
    const providers = useSelector(({ providers }) => providers.providers.list);
    const allCredentials = useSelector(({ credentials }) => credentials.credential.list);

    // Auto-fetch if Redux state is empty, but ask AT MOST ONCE per mount.
    //
    // These two effects used to be keyed on the very lists they fetch, with no
    // record of having already asked. That is an infinite loop whenever the
    // answer is legitimately empty: the request succeeds, the reducer stores
    // `response.data` - a BRAND NEW empty array - the dependency therefore
    // changes, the effect runs again, sees length 0 again, and fires another
    // request. It never converges, and it never even pauses, so the browser
    // queues requests until Chrome refuses to open more sockets
    // (`net::ERR_INSUFFICIENT_RESOURCES`) and the tab locks up hard enough that
    // devtools will not open.
    //
    // It survived because it only bites when a list comes back EMPTY. Anyone
    // who can see at least one credential gets a single fetch and never
    // notices. An organization that has no credentials yet - which is every
    // organization on the day it is created - opens the cluster form and
    // freezes the browser. Reported on a staging org 2026-09-14.
    //
    // A ref, not state: flipping state here would itself re-render and we are
    // trying to stop a render loop, not add one.
    const requestedProviders = useRef(false);
    const requestedCredentials = useRef(false);

    useEffect(() => {
        if (requestedProviders.current) return;
        if (!providers || providers.length === 0) {
            requestedProviders.current = true;
            dispatch(getProviders());
        }
    }, [dispatch, providers]);

    useEffect(() => {
        if (requestedCredentials.current) return;
        if (!allCredentials || allCredentials.length === 0) {
            requestedCredentials.current = true;
            dispatch(getCredentials());
        }
    }, [dispatch, allCredentials]);

    const provider = useMemo(
        () => providers?.find((p) => p.id === providerId) || null,
        [providers, providerId]
    );

    const credentials = useMemo(
        () => (allCredentials || []).filter((c) => c.provider === providerId),
        [allCredentials, providerId]
    );

    const regions = useMemo(() => provider?.regions || [], [provider]);

    const instanceSizes = useMemo(() => provider?.instanceSizes || [], [provider]);

    const volSizeLimits = useMemo(() => provider?.volsize || { min: 100, max: 16000 }, [provider]);

    const zonesForRegion = useCallback(
        (regionId) => {
            if (!regionId || !regions.length) return [];
            const region = regions.find((r) => r.region_id === regionId);
            return region?.zones || [];
        },
        [regions]
    );

    return { credentials, regions, instanceSizes, volSizeLimits, zonesForRegion };
}
